import { distanceMeters, weightedAverage } from "./mathStuff"
import buffaloRiverPath from "../data/buffaloRiverPath.json"

export type RangeDef = {
    min: number,
    max: number,
    category: "Very Low" | "Low" | "Moderate" | "High" | "Flood"
}

type DischargeLookup = {
    [sensorId: string]: number
}

export type DischargeLookupsByTime = {
    [utcTimeString: string]: DischargeLookup
}

export const boxleySensorId = "USGS-07055646"
export const boxleySensorCoords = [ -93.405, 35.9388888888889 ]
export const boxleySensorRiverDistanceMeters = 22224.736582598733
export const boxleySensorRangeSet: RangeDef[] = [
  { min: -Infinity, max: 200, category: "Very Low" },
  { min: 200, max: 350, category: "Low" },
  { min: 350, max: 1500, category: "Moderate" },
  { min: 1500, max: 6500, category: "High" },
  { min: 6500, max: Infinity, category: "Flood" }
]

export const poncaSensorId = "USGS-07055660"
export const poncaSensorCoords = [ -93.3547222222222, 36.0225 ]
export const poncaSensorRiverDistanceMeters = 34603.730462740736
export const poncaSensorRangeSet: RangeDef[] = [
  { min: -Infinity, max: 100, category: "Very Low" },
  { min: 100, max: 200, category: "Low" },
  { min: 200, max: 900, category: "Moderate" },
  { min: 900, max: 1600, category: "High" },
  { min: 1600, max: Infinity, category: "Flood" }
]

export const pruittSensorId = "USGS-07055680"
export const pruittSensorCoords = [ -93.13777777777779, 36.05916666666666 ]
export const pruittSensorRiverDistanceMeters = 71233.67026145301
export const pruittSensorRangeSet: RangeDef[] = [
  { min: -Infinity, max: 100, category: "Very Low" },
  { min: 100, max: 200, category: "Low" },
  { min: 200, max: 1000, category: "Moderate" },
  { min: 1000, max: 2000, category: "High" },
  { min: 2000, max: Infinity, category: "Flood" }
]

export const stJoeSensorId = "USGS-07056000"
export const stJoeSensorCoords = [ -92.74722222222222, 35.98305555555556 ]
export const stJoeSensorRiverDistanceMeters = 140151.48076935374
export const stJoeSensorRangeSet: RangeDef[] = [
  { min: -Infinity, max: 40, category: "Very Low" },
  { min: 40, max: 200, category: "Low" },
  { min: 200, max: 3000, category: "Moderate" },
  { min: 3000, max: 8000, category: "High" },
  { min: 8000, max: Infinity, category: "Flood" }
]

export const harrietSensorId = "USGS-07056700"
export const harrietSensorCoords = [ -92.5775, 36.06777777777778 ]
export const harrietSensorRiverDistanceMeters = 181555.1595339605
export const harrietSensorRangeSet: RangeDef[] = [
  { min: -Infinity, max: 60, category: "Very Low" },
  { min: 60, max: 200, category: "Low" },
  { min: 200, max: 3000, category: "Moderate" },
  { min: 3000, max: 9370, category: "High" },
  { min: 9370, max: Infinity, category: "Flood" }
]

const allSensorIds = [boxleySensorId, poncaSensorId, pruittSensorId, stJoeSensorId, harrietSensorId]
const maxFloatabilityChangePerSegment = 1

const dischardParameterCode = "00060"
const getLatestDischargeValuesRequestUrl = `https://api.waterdata.usgs.gov/ogcapi/v0/collections/latest-continuous/items?f=json&lang=en-US&limit=10&properties=monitoring_location_id,value&skipGeometry=true&offset=0&monitoring_location_id=${boxleySensorId}%2C${poncaSensorId}%2C${pruittSensorId}%2C${stJoeSensorId}%2C${harrietSensorId}&parameter_code=${dischardParameterCode}`
// 1 data point every 15min
// 24hrs = 1440min => 96 data points per day per sensor
// 5 sensors * 96 = 475 data points per day total
export const numHoursPriorToCapture = 48
const dataLimit = 1000
// windowEnd is always now in production. Debug tooling can move it into the
// past to replay older sensor data through the same 48hr window.
const getTimeSeriesDischargeValuesRequestUrl = (windowEnd: Date) => `https://api.waterdata.usgs.gov/ogcapi/v0/collections/continuous/items?f=json&lang=en-US&limit=${dataLimit}&properties=monitoring_location_id,value,time&skipGeometry=true&offset=0&datetime=${getRequestDateRange(numHoursPriorToCapture, windowEnd)}&monitoring_location_id=${boxleySensorId}%2C${poncaSensorId}%2C${pruittSensorId}%2C${stJoeSensorId}%2C${harrietSensorId}&parameter_code=${dischardParameterCode}`

function getRequestDateRange(hoursPriorToEnd: number, end: Date) {
  const start = new Date(end.getTime() - hoursPriorToEnd * 60 * 60 * 1000)

  const iso = (date: Date) => date.toISOString().replace(/\.\d{3}Z$/, 'Z')

  const result = encodeURIComponent(`${iso(start)}/${iso(end)}`)
  return result
}

function calculateWeight(
  reference: number[],
  targetA: number[],
  targetB: number[]
) {
  const dA = distanceMeters(reference[1], reference[0], targetA[1], targetA[0]);
  const dB = distanceMeters(reference[1], reference[0], targetB[1], targetB[0]);

  if (dA + dB === 0) 
    return 0.5

  return dA / (dA + dB)
}

function blendRanges(rangeSetA: RangeDef[], rangeSetB: RangeDef[], weightA = 0.5) {
  if (rangeSetA.length !== rangeSetB.length) {
    throw new Error("Range sets must have the same number of categories");
  }

  const weightB = 1 - weightA;

  return rangeSetA.map((a, i) => {
    const b = rangeSetB[i];

    return {
      category: a.category,
      min:
        Number.isFinite(a.min) && Number.isFinite(b.min)
          ? a.min * weightA + b.min * weightB
          : a.min,
      max:
        Number.isFinite(a.max) && Number.isFinite(b.max)
          ? a.max * weightA + b.max * weightB
          : a.max
    };
  });
}

function calculateInterpolatedFloatabilityScore(dischargeValue: number, rangeSet: RangeDef[]) {
  const lastIndex = rangeSet.length - 1;

  for (let i = 0; i < rangeSet.length; i++) {
    const { min, max } = rangeSet[i];

    if (dischargeValue >= min && dischargeValue < max) {
      // First bucket
      if (i === 0) {
        if (!Number.isFinite(max)) return 0;

        const t = (dischargeValue - Math.max(dischargeValue, min)) / 1;
        return 0;
      }

      // Last bucket
      if (i === lastIndex) {
        return 1000;
      }

      const center = i / lastIndex;

      const lowerBound = center - 0.5 / lastIndex;
      const upperBound = center + 0.5 / lastIndex;

      const t = (dischargeValue - min) / (max - min);

      return Math.round((lowerBound + t * (upperBound - lowerBound))*1000);
    }
  }

  throw new Error("Value does not fall into any range");
}

export function calculateFloatability(coords: number[], riverDistance: number, dischargeLookup: { [key: string]: number }): number {
  let result = -1

  let coordsA: number[] = [0,0]
  let coordsB: number[] | null = null
  let rangeSetA: RangeDef[] = boxleySensorRangeSet
  let rangeSetB: RangeDef[] | null = null
  let dischargeValueA: number = dischargeLookup[boxleySensorId]
  let dischargeValueB: number | null = null

  if (riverDistance < boxleySensorRiverDistanceMeters) {
    coordsA = boxleySensorCoords
    rangeSetA = boxleySensorRangeSet
    dischargeValueA = dischargeLookup[boxleySensorId]
    coordsB = null
    rangeSetB = null
    dischargeValueB = null
  } else if (
    riverDistance >= boxleySensorRiverDistanceMeters && 
    riverDistance < poncaSensorRiverDistanceMeters
  ) {
    coordsA = boxleySensorCoords
    rangeSetA = boxleySensorRangeSet
    dischargeValueA = dischargeLookup[boxleySensorId]
    coordsB = poncaSensorCoords
    rangeSetB = poncaSensorRangeSet
    dischargeValueB = dischargeLookup[poncaSensorId]
  } else if (
    riverDistance >= poncaSensorRiverDistanceMeters && 
    riverDistance < pruittSensorRiverDistanceMeters
  ) {
    coordsA = poncaSensorCoords
    rangeSetA = poncaSensorRangeSet
    dischargeValueA = dischargeLookup[poncaSensorId]
    coordsB = pruittSensorCoords
    rangeSetB = pruittSensorRangeSet
    dischargeValueB = dischargeLookup[pruittSensorId]
  } else if (
    riverDistance >= pruittSensorRiverDistanceMeters && 
    riverDistance < stJoeSensorRiverDistanceMeters
  ) {
    coordsA = pruittSensorCoords
    rangeSetA = pruittSensorRangeSet
    dischargeValueA = dischargeLookup[pruittSensorId]
    coordsB = stJoeSensorCoords
    rangeSetB = stJoeSensorRangeSet
    dischargeValueB = dischargeLookup[stJoeSensorId]
  } else if (
    riverDistance >= stJoeSensorRiverDistanceMeters && 
    riverDistance < harrietSensorRiverDistanceMeters
  ) {
    coordsA = stJoeSensorCoords
    rangeSetA = stJoeSensorRangeSet
    dischargeValueA = dischargeLookup[stJoeSensorId]
    coordsB = harrietSensorCoords
    rangeSetB = harrietSensorRangeSet
    dischargeValueB = dischargeLookup[harrietSensorId]
  } else if (riverDistance >= harrietSensorRiverDistanceMeters) {
    coordsA = harrietSensorCoords
    rangeSetA = harrietSensorRangeSet
    dischargeValueA = dischargeLookup[harrietSensorId]
    coordsB = null
    rangeSetB = null
    dischargeValueB = null
  }

  const only1SensorAvailable = coordsB === null && rangeSetB === null && dischargeValueB === null
  if (only1SensorAvailable) {
    result = calculateInterpolatedFloatabilityScore(
      dischargeValueA,
      rangeSetA
    )
  } else if (coordsB !== null && rangeSetB !== null && dischargeValueB !== null){
    const weightA = calculateWeight(
      coords,
      coordsB,
      coordsA
    )
    const weightedDischargeValue = weightedAverage(dischargeValueA, dischargeValueB, weightA)
    const blendedRangeSet = blendRanges(rangeSetA, rangeSetB, weightA)
    result = calculateInterpolatedFloatabilityScore(
      weightedDischargeValue,
      blendedRangeSet
    )
  }

  return result
}

const fillInLookupGaps = (lookUpToUpdate: DischargeLookup, completeLookUp: DischargeLookup) => {
  allSensorIds.forEach(id => {
    if (lookUpToUpdate[id] == null)
      lookUpToUpdate[id] = completeLookUp[id]
  })
}

async function getDishargeLookupsByTime(windowEnd: Date) {
  const result: DischargeLookupsByTime = {}

    try {
        const response = await fetch(getTimeSeriesDischargeValuesRequestUrl(windowEnd))

        if (!response.ok) {
            throw new Error(`HTTP error: ${response.status}`)
        }

        const data = await response.json()
        const features: any[] = data.features
        
        features.forEach(feature => {
            const sensorId: string = feature.properties.monitoring_location_id
            const value = parseFloat(feature.properties.value)
            const time: string = feature.properties.time

            if (result[time])
              result[time][sensorId] = value
            else
              result[time] = { [sensorId]: value }
        })

        let i = 0
        let latestLookupWithAllData: DischargeLookup = {}
        for (const [_time, lookup] of Object.entries(result)) {
          const isDataMissing = Object.keys(lookup).length !== allSensorIds.length

          if (i==0 && isDataMissing) {
            let lookupWithAllData: DischargeLookup | null = null
            const allLookups = Object.values(result)
            for(let j=1; lookupWithAllData === null && j<allLookups.length; j++) {
              if (Object.keys(allLookups[j]).length === allSensorIds.length)
                lookupWithAllData = allLookups[j]
            }
            if (lookupWithAllData !== null) {
              fillInLookupGaps(lookup, lookupWithAllData)
            } else {
              throw new Error("Cannot find discharge data for sensor(s)")
            }
          } else if (isDataMissing) {
            fillInLookupGaps(lookup, latestLookupWithAllData)
          }
          latestLookupWithAllData = lookup
          i+=1
        }

    } catch (error) {
        console.error('API call failed:', error)
    } finally {
        return result
    }
}

const getPolylineFloatabilityByTimeStringIndex = (
  utcTimeStrings: string[], 
  dischargeLookupsByTime: DischargeLookupsByTime
) => {
  const result: number[][] = []
  for(let i=0; i<utcTimeStrings.length; i++) {
    result[i] = []
  }

  for(let i=0; i<utcTimeStrings.length; i++) {
    const timeString = utcTimeStrings[i]
    let currentDistance = 0
    let lastFloatabilityScore = -1
    for(let j=0; j<buffaloRiverPath.length-1; j++) {
      const segmentDistance = distanceMeters(
          buffaloRiverPath[j][1],buffaloRiverPath[j][0],
          buffaloRiverPath[j+1][1],buffaloRiverPath[j+1][0],
      )
      currentDistance += segmentDistance

      let floatability = calculateFloatability(
        buffaloRiverPath[j+1], 
        currentDistance, 
        dischargeLookupsByTime[timeString]
      )
      if (lastFloatabilityScore !== -1) {
          const diff = floatability-lastFloatabilityScore
          if (diff >= maxFloatabilityChangePerSegment)
              floatability = lastFloatabilityScore + maxFloatabilityChangePerSegment
          else if (diff <= -maxFloatabilityChangePerSegment)
              floatability = lastFloatabilityScore - maxFloatabilityChangePerSegment
      }
      lastFloatabilityScore = floatability

      result[i][j] = floatability
    }
  }

  return result
}

export async function getTimeStringsAndFloatabilityScores(windowEnd: Date = new Date()) {

  const dischargeLookupsByTime = await getDishargeLookupsByTime(windowEnd)

  const utcTimeStrings = Object.keys(dischargeLookupsByTime).map(time => time)
  if (utcTimeStrings.length === 0)
    throw new Error("No discharge data returned for the requested window")
  utcTimeStrings.sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
  const utcTimeStringsOnlyHourly =  [
    ...utcTimeStrings
      .slice(0, utcTimeStrings.length-1)
      .filter(utc => new Date(utc).getUTCMinutes() === 0),
    utcTimeStrings[utcTimeStrings.length-1], 
  ]
  // human readable strings for the available times of sensor readings
  const timeStrings = utcTimeStringsOnlyHourly.map(utc => new Date(utc).toLocaleString())
  
  // 1st order array -> index corresponds to the timeString index above
  // 2nd order array -> floatability scores for each polyline where the index = ObjectID
  const polylineFloatabilityByTimeStringIndex: number[][] = getPolylineFloatabilityByTimeStringIndex(
    utcTimeStringsOnlyHourly,
    dischargeLookupsByTime
  )

  return {timeStrings, polylineFloatabilityByTimeStringIndex}
}